import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import TextChangeSchema from "work/proposals/redlining/__diff/_schema/text-change-schema";
import { TextChangeCollection } from "work/proposals/redlining/__diff/text-change";
import RedlineChangeSchema from "work/proposals/redlining/changes/_schemas/redline-change-schema";
import { RedlineAction, RedlineChange } from "work/proposals/redlining/changes/redline-change";
import { z } from "zod";

export type FieldRedlineJSON = z.infer<typeof FieldRedlineSchema.FieldRedlineJSONZ>;
export type FieldRedlineParams = z.infer<typeof FieldRedlineSchema.FieldRedlineZ>;

export type FieldRedlineArrayJSON = z.infer<typeof FieldRedlineSchema.FieldRedlineArrayJSONZ>;
export type FieldRedlineArrayParams = z.infer<typeof FieldRedlineSchema.FieldRedlineArrayZ>;

const JsonValueZ = z.union([
  z.string(), 
  z.number(), 
  z.boolean(), 
  z.record(z.string(), z.unknown()), 
  z.null()
]);

export default class FieldRedlineSchema {
  public static readonly FieldRedlineJSONZ = z.object({
    originalEntry: JsonValueZ,
    revisedEntry: JsonValueZ,
    currentEntry: z.union([JsonValueZ, z.undefined()]),
    sessionHistory: z.array(RedlineChangeSchema.RedlineChangeJSONZ).nullish(),
    diffWords: z.boolean(),
    textChangeCollection: TextChangeSchema.TextChangeCollectionJSONZ.nullish(),
  });

  public static readonly FieldRedlineZ = z.object({
    originalEntry: z.any().nullable(),
    revisedEntry: z.any().nullable(),
    currentEntry: z.any().nullish(),
    sessionHistory: z.array(z.instanceof(RedlineChange)).optional(),
    diffWords: z.boolean(),
    textChangeCollection: z.instanceof(TextChangeCollection).optional(),
    latestAction: z.instanceof(RedlineAction).optional(),
  });

  public static readonly FieldRedlineArrayJSONZ = z.object({
    redlines: z.array(FieldRedlineSchema.FieldRedlineJSONZ),
    sessionHistory: z.array(RedlineChangeSchema.RedlineChangeJSONZ),
  });

  public static readonly FieldRedlineArrayZ = z.object({
    redlines: z.array(FieldRedlineSchema.FieldRedlineZ),
    sessionHistory: z.array(RedlineChangeSchema.RedlineChangeZ),
  });

  public static validate(values: unknown): FieldRedlineParams {
    return parseOrThrow(FieldRedlineSchema.FieldRedlineZ, values, {
      logError: true,
      schemaName: "FieldRedlineSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): FieldRedlineJSON {
    return parseOrThrow(FieldRedlineSchema.FieldRedlineJSONZ, json, {
      logError: true,
      schemaName: "FieldRedlineSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is FieldRedlineParams {
    try {
      FieldRedlineSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is FieldRedlineJSON {
    try {
      FieldRedlineSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(params: FieldRedlineJSON, field: ProposalField): FieldRedlineParams {
    if (isNullOrUndefined(params)) return params as FieldRedlineParams;
    const parsed = parseOrThrow(FieldRedlineSchema.FieldRedlineJSONZ, params, {
      logError: true,
      schemaName: "FieldRedlineSchema",
      operation: "deserialize",
      payloadSample: params,
    });
    return FieldRedlineSchema.fieldRedlineCodec.decode({ data: parsed, field });
  }

  public static deserializeArray(json: FieldRedlineArrayJSON, field: ProposalField): FieldRedlineArrayParams {
    if (isNullOrUndefined(json)) return json as FieldRedlineArrayParams;

    const parsed = parseOrThrow(FieldRedlineSchema.FieldRedlineArrayJSONZ, json, {
      logError: true,
      schemaName: "FieldRedlineSchema",
      operation: "deserializeArray",
      payloadSample: json,
    });
    const redlines = parsed.redlines.map((redlineJSON) =>
      FieldRedlineSchema.fieldRedlineCodec.decode({ data: redlineJSON, field })
    );
    const sessionHistory = parsed.sessionHistory.map((change) => 
      RedlineChangeSchema.deserialize(change)
    );
    return { redlines, sessionHistory };
  }

  public static serialize(params: FieldRedlineParams): FieldRedlineJSON {
    if (isNullOrUndefined(params)) return params as FieldRedlineJSON;
    const parsed = FieldRedlineSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed as FieldRedlineJSON;
    return FieldRedlineSchema.fieldRedlineCodec.encode(parsed).data;
  }

  private static readonly fieldRedlineCodec = z.codec(
    z.custom<{ data: FieldRedlineJSON; field?: ProposalField | null }>(),
    FieldRedlineSchema.FieldRedlineZ,
    {
      decode: (params: { data: FieldRedlineJSON; field?: ProposalField | null }) => {
        const { data, field } = params;
        if (!field) {
          throw new Error("FieldRedlineSchema: Missing 'field: ProposalField' property for decoding FieldRedline.");
        }
        return {
          originalEntry: field.fromJSON(data.originalEntry),
          revisedEntry: field.fromJSON(data.revisedEntry),
          currentEntry: data.currentEntry === undefined
            ? undefined
            : field.fromJSON(data.currentEntry),
          sessionHistory: data.sessionHistory?.map((change) => RedlineChange.fromJSON({ json: change, field })),
          diffWords: data.diffWords,
          textChangeCollection: TextChangeCollection.fromJSONArray(data.textChangeCollection?.changes),
        };
      },
      encode: (value) => ({
        data: {
          originalEntry: value.originalEntry?.toJSON() ?? null,
          revisedEntry: value.revisedEntry?.toJSON() ?? null,
          currentEntry: isNullOrUndefined(value.currentEntry) ? value.currentEntry : value.currentEntry.toJSON(),
          sessionHistory: value.sessionHistory?.map((change) => RedlineChangeSchema.serialize(change)),
          diffWords: value.diffWords,
          textChangeCollection: value.textChangeCollection?.toJSON(),
        },
        field: null as ProposalField | null,
      }),
    }
  );
}
